import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Button, Chip, Grid, MenuItem, Paper, Typography, Stack, List, ListItem, ListItemText,
  FormControl, InputLabel, Select, TextField, Alert, CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import { GET_EXERCISES, CREATE_EXERCISE } from '../queries';

const CATEGORIES = ['CHEST', 'BACK', 'LEGS', 'SHOULDERS', 'ARMS', 'CARDIO', 'CORE', 'PLYOMETRICS'];

interface FormInputs {
  name: string;
  category: string;
  equipment: string;
}

export const ExerciseManager: React.FC = () => {
  const { data, loading, error, refetch } = useQuery(GET_EXERCISES);
  const [createExercise] = useMutation(CREATE_EXERCISE);

  const { control, handleSubmit, reset, formState: { isSubmitting } } = useForm<FormInputs>({
    defaultValues: { name: '', category: 'CHEST', equipment: 'BARBELL' },
  });

  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);

  const exercises = data?.getExercises || [];

  const onSubmit = (formData: FormInputs) => {
    if (!formData.name.trim()) return;

    setCreateError(null);
    reset();

    createExercise({
      variables: {
        input: {
          name: formData.name.trim(),
          category: formData.category,
          equipment: formData.equipment.toUpperCase().trim(),
        },
      },
      optimisticResponse: {
        createExercise: {
          __typename: 'Exercise',
          id: 'temp-ex-id-' + Date.now(),
          name: formData.name.trim(),
          category: formData.category,
          equipment: formData.equipment.toUpperCase().trim(),
          isStandard: false,
        },
      },
      update(cache, { data: mutationData }) {
        const newEx = mutationData?.createExercise;
        if (!newEx) return;
        const existing: any = cache.readQuery({ query: GET_EXERCISES });
        if (existing) {
          cache.writeQuery({
            query: GET_EXERCISES,
            data: { getExercises: [...existing.getExercises, newEx] },
          });
        }
      },
    }).catch((err: any) => {
      setCreateError(err.message || 'Error creating exercise');
    });
  };

  const filteredExercises = selectedCategoryFilter
    ? exercises.filter((ex: any) => ex.category === selectedCategoryFilter)
    : exercises;

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">Failed to load exercises: {error.message}</Alert>;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Add Custom Exercise</Typography>
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              {createError && <Alert severity="error">{createError}</Alert>}
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Name is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Exercise Name"
                    error={!!error}
                    helperText={error ? error.message : ''}
                    required
                    fullWidth
                    size="small"
                  />
                )}
              />
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel id="category-select-label">Category</InputLabel>
                    <Select {...field} labelId="category-select-label" label="Category">
                      {CATEGORIES.map(cat => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              <Controller
                name="equipment"
                control={control}
                rules={{ required: 'Equipment is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Equipment"
                    error={!!error}
                    helperText={error ? error.message : ''}
                    required
                    fullWidth
                    size="small"
                    placeholder="e.g. BARBELL, DUMBBELL, MACHINE, BODYWEIGHT"
                  />
                )}
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={isSubmitting}
                fullWidth
              >
                {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Create Exercise'}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Grid>
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Exercise Library</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip
              label="ALL"
              clickable
              color={selectedCategoryFilter === null ? 'primary' : 'default'}
              onClick={() => setSelectedCategoryFilter(null)}
            />
            {CATEGORIES.map(cat => (
              <Chip
                key={cat}
                label={cat}
                clickable
                color={selectedCategoryFilter === cat ? 'primary' : 'default'}
                onClick={() => setSelectedCategoryFilter(cat)}
              />
            ))}
          </Box>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredExercises.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No exercises found.</Typography>
            ) : (
              <List dense>
                {filteredExercises.map((ex: any) => (
                  <ListItem
                    key={ex.id}
                    divider
                    secondaryAction={
                      <Chip
                        label={ex.isStandard ? 'Standard' : 'Custom'}
                        size="small"
                        color={ex.isStandard ? 'secondary' : 'default'}
                        variant="outlined"
                      />
                    }
                  >
                    <ListItemText
                      primary={ex.name}
                      secondary={`${ex.category} • ${ex.equipment}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};
